import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import './PuzzleBoard.css';

// Interactive 9x9 board for puzzle solving. Independent of GameBoard.js
// (which is 1600 lines and tightly coupled to full-game state). Pattern
// mirrors EndgameTrainer's InteractiveBoard — click a piece to select,
// click a destination to move. No move-legality checks here: the puzzle
// pipeline validates against the recorded solution, so illegal moves
// simply get marked "incorrect" rather than being rejected client-side.
const PIECE_IMAGES = {
  white: {
    king: '/assets/pieces/white-king.png',    knight: '/assets/pieces/white-knight.png',
    pawn: '/assets/pieces/white-pawn.png',    advisor: '/assets/pieces/white-advisor.png',
    cannon: '/assets/pieces/white-cannon.png', camel: '/assets/pieces/white-camel.png',
    donkey: '/assets/pieces/white-donkey.png', ram: '/assets/pieces/white-ram.png',
    spy: '/assets/pieces/white-spy.png',
  },
  black: {
    king: '/assets/pieces/black-king.png',    knight: '/assets/pieces/black-knight.png',
    pawn: '/assets/pieces/black-pawn.png',    advisor: '/assets/pieces/black-advisor.png',
    cannon: '/assets/pieces/black-cannon.png', camel: '/assets/pieces/black-camel.png',
    donkey: '/assets/pieces/black-donkey.png', ram: '/assets/pieces/black-ram.png',
    spy: '/assets/pieces/black-spy.png',
  },
};

const InteractiveBoard = ({ board, selected, lastMove, hintFrom, hintTo, onSquareClick, disabled }) => {
  const cells = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const piece = board && board[r] && board[r][c];
      const isDark      = (r + c) % 2 === 1;
      const isSelected  = selected && selected.r === r && selected.c === c;
      const isLastFrom  = lastMove && lastMove.fromRow === r && lastMove.fromCol === c;
      const isLastTo    = lastMove && lastMove.toRow   === r && lastMove.toCol   === c;
      const isHintFrom  = hintFrom && hintFrom.r === r && hintFrom.c === c;
      const isHintTo    = hintTo   && hintTo.r   === r && hintTo.c   === c;
      const clickable   = !disabled && !!onSquareClick;
      const cls = [
        'et-square',
        isDark ? 'et-dark' : 'et-light',
        isSelected && 'et-selected',
        isLastFrom && 'et-reveal-from',
        isLastTo   && 'et-reveal-to',
        isHintFrom && 'pz-hint-from',
        isHintTo   && 'pz-hint-to',
        clickable  && 'et-clickable',
      ].filter(Boolean).join(' ');
      cells.push(
        <div
          key={`${r}-${c}`}
          className={cls}
          onClick={clickable ? () => onSquareClick(r, c) : undefined}
        >
          {piece && <img src={PIECE_IMAGES[piece.color][piece.type]} alt="" draggable={false} />}
        </div>
      );
    }
  }
  return <div className="et-board">{cells}</div>;
};

// Ranga FEN — 9 ranks separated by "/", each rank is 9 chars of piece
// letters (KNMACDRSP, uppercase=white, lowercase=black) or "." for empty
// or digit-run (chess-FEN style).
function parseFEN(fen) {
  const CHAR_TO_TYPE = {
    K: 'king', N: 'knight', M: 'camel', A: 'advisor', C: 'cannon',
    D: 'donkey', R: 'ram', S: 'spy', P: 'pawn',
  };
  const board = Array(9).fill(null).map(() => Array(9).fill(null));
  if (!fen || typeof fen !== 'string') return board;
  const ranks = fen.trim().split('/');
  if (ranks.length !== 9) return board;
  for (let r = 0; r < 9; r++) {
    let col = 0;
    for (const ch of ranks[r]) {
      if (col >= 9) break;
      if (ch === '.') { col++; continue; }
      if (ch >= '1' && ch <= '9') { col += parseInt(ch, 10); continue; }
      const type = CHAR_TO_TYPE[ch.toUpperCase()];
      if (!type) continue;
      board[r][col] = { type, color: (ch === ch.toUpperCase()) ? 'white' : 'black' };
      col++;
    }
  }
  return board;
}

// Apply a move to a board (returns a NEW board, doesn't mutate).
function applyMove(board, mv) {
  const next = board.map(row => row.slice());
  const src = next[mv.fromRow][mv.fromCol];
  next[mv.toRow][mv.toCol] = src;
  next[mv.fromRow][mv.fromCol] = null;
  return next;
}

// Parse the miner's solution format: "R,C,R,C|R,C,R,C|..." where each
// element between "|" is one move as 4 comma-separated ints.
function parseSolution(str) {
  if (!str) return [];
  return str.split('|').map(m => {
    const parts = m.split(',').map(Number);
    if (parts.length !== 4 || parts.some(n => Number.isNaN(n))) return null;
    const [fromRow, fromCol, toRow, toCol] = parts;
    return { fromRow, fromCol, toRow, toCol };
  }).filter(Boolean);
}

const OPPONENT_REPLY_DELAY_MS = 500;

const PuzzleBoard = ({ puzzleId, onBack, onComplete }) => {
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [board, setBoard] = useState(null);
  const [solution, setSolution] = useState([]);   // parsed [{fromRow,...}]
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [status, setStatus] = useState('playing'); // 'playing' | 'wrong' | 'completed' | 'awaiting-opponent'
  const [startTime, setStartTime] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [hintLevel, setHintLevel] = useState(0);   // 0=none, 1=from-square, 2=full move
  const [hintsUsed, setHintsUsed] = useState(0);   // total hint clicks this attempt (survives puzzle progression)
  const [sttmIsWhite, setStmIsWhite] = useState(true);  // side to move on the *puzzle* (position at load)

  const resetToStart = useCallback((puz) => {
    setBoard(parseFEN(puz.fen_position));
    setSolution(parseSolution(puz.solution_moves));
    setCurrentMoveIndex(0);
    setSelected(null);
    setLastMove(null);
    setStatus('playing');
    setStartTime(Date.now());
    setHintLevel(0);
    setHintsUsed(0);
    // STM inference: if description says "STM (white)" pull white, else black.
    // Mined puzzles set this in the description; fall back to white.
    const s = puz.description && /STM \((white|black)\)/i.exec(puz.description);
    setStmIsWhite(s ? s[1].toLowerCase() === 'white' : true);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const puzzleData = await api.getPuzzle(puzzleId);
        setPuzzle(puzzleData);
        resetToStart(puzzleData);
      } catch (err) {
        console.error('Error loading puzzle:', err);
        setError('Failed to load puzzle');
      } finally {
        setLoading(false);
      }
    })();
  }, [puzzleId, resetToStart]);

  const [ratingChange, setRatingChange] = useState(null);
  const submitAttempt = useCallback(async (completed, timeTaken, hintsUsedCount = 0) => {
    try {
      const resp = await api.submitPuzzleAttempt(puzzleId, completed, timeTaken, hintsUsedCount);
      if (resp && resp.rating) setRatingChange(resp.rating);
    } catch (err) { console.error('Error submitting attempt:', err); }
  }, [puzzleId]);

  const attemptMove = useCallback((fromR, fromC, toR, toC) => {
    const expected = solution[currentMoveIndex];
    if (!expected) return;
    const correct = expected.fromRow === fromR && expected.fromCol === fromC
                 && expected.toRow   === toR   && expected.toCol   === toC;
    if (!correct) {
      setAttempts(a => a + 1);
      setStatus('wrong');
      setSelected(null);
      setTimeout(() => setStatus('playing'), 1200);
      return;
    }
    // Correct move — apply, advance, maybe auto-play opponent's reply.
    const mv = { fromRow: fromR, fromCol: fromC, toRow: toR, toCol: toC };
    setBoard(b => applyMove(b, mv));
    setLastMove(mv);
    setSelected(null);
    setHintLevel(0);   // Clear hint markers between plies (they belong to a specific expected move).
    const nextIdx = currentMoveIndex + 1;
    setCurrentMoveIndex(nextIdx);

    if (nextIdx >= solution.length) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setStatus('completed');
      submitAttempt(true, elapsed, hintsUsed);
      if (onComplete) setTimeout(() => onComplete(), 1800);
      return;
    }
    // Auto-play opponent's response after a short delay so the user sees it.
    setStatus('awaiting-opponent');
    setTimeout(() => {
      const oppMv = solution[nextIdx];
      setBoard(b => applyMove(b, oppMv));
      setLastMove(oppMv);
      setCurrentMoveIndex(nextIdx + 1);
      setStatus(nextIdx + 1 >= solution.length ? 'completed' : 'playing');
      if (nextIdx + 1 >= solution.length) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        submitAttempt(true, elapsed, hintsUsed);
        if (onComplete) setTimeout(() => onComplete(), 1800);
      }
    }, OPPONENT_REPLY_DELAY_MS);
  }, [solution, currentMoveIndex, startTime, submitAttempt, onComplete, hintsUsed]);

  const handleSquareClick = useCallback((r, c) => {
    if (status !== 'playing' || !board) return;
    const piece = board[r][c];
    // Whose turn: side-to-move flips each ply from the initial position.
    // Even move-index → puzzle's STM to move; odd → opponent.
    const puzzleStmColor = sttmIsWhite ? 'white' : 'black';
    const oppColor       = sttmIsWhite ? 'black' : 'white';
    const turnColor = (currentMoveIndex % 2 === 0) ? puzzleStmColor : oppColor;
    if (!selected) {
      if (piece && piece.color === turnColor) setSelected({ r, c });
      return;
    }
    if (selected.r === r && selected.c === c) { setSelected(null); return; }
    // Reselect another own piece.
    if (piece && piece.color === turnColor) { setSelected({ r, c }); return; }
    // Second click on target — attempt the move.
    attemptMove(selected.r, selected.c, r, c);
  }, [status, board, selected, currentMoveIndex, sttmIsWhite, attemptMove]);

  const resetPuzzle = () => {
    if (puzzle) resetToStart(puzzle);
  };

  const skipPuzzle = () => {
    const elapsed = Math.floor((Date.now() - (startTime || Date.now())) / 1000);
    submitAttempt(false, elapsed, hintsUsed);
    onBack();
  };

  const revealHint = () => {
    // Progressive hint: first click reveals the from-square (which piece
    // moves), second click also reveals the to-square (full move). After
    // that clicking is a no-op — reset the puzzle to try again from scratch
    // if the user wants a fresh attempt. Increment hintsUsed each click for
    // tracking; hintLevel is the display state within the current expected move.
    setHintLevel(prev => Math.min(prev + 1, 2));
    setHintsUsed(prev => prev + 1);
  };

  // Compute hint square coordinates for the *current* expected move.
  const currentExpected = solution[currentMoveIndex];
  const hintFrom = (hintLevel >= 1 && currentExpected && status === 'playing')
    ? { r: currentExpected.fromRow, c: currentExpected.fromCol } : null;
  const hintTo = (hintLevel >= 2 && currentExpected && status === 'playing')
    ? { r: currentExpected.toRow, c: currentExpected.toCol } : null;

  if (loading) {
    return (
      <div className="puzzle-board-loading">
        <h2>Loading puzzle...</h2>
      </div>
    );
  }
  if (error) {
    return (
      <div className="puzzle-board-error">
        <h2>{error}</h2>
        <button onClick={onBack} className="back-button">Back to Puzzles</button>
      </div>
    );
  }
  if (!puzzle || !board) return null;

  const puzzleStmLabel = sttmIsWhite ? 'White' : 'Black';
  const turnIsUser = currentMoveIndex % 2 === 0;

  return (
    <div className="puzzle-board-container">
      <div className="puzzle-board-header">
        <button onClick={onBack} className="back-button">← Back to Puzzles</button>
        <div className="puzzle-info">
          <h2>{puzzle.title}</h2>
          <p className="puzzle-instruction">{puzzle.description}</p>
          <div className="puzzle-meta">
            <span className="difficulty">{puzzle.difficulty}</span>
            <span className="rating">Rating: {puzzle.rating}</span>
            <span className="attempts">Attempts: {attempts}</span>
          </div>
        </div>
      </div>

      <div className="puzzle-status">
        {status === 'playing' && turnIsUser && (
          <div className="status-message playing">
            {puzzleStmLabel} to move — find the winning move
            (move {Math.floor(currentMoveIndex / 2) + 1} of {Math.ceil(solution.length / 2)}).
          </div>
        )}
        {status === 'awaiting-opponent' && (
          <div className="status-message playing">Opponent responding…</div>
        )}
        {status === 'wrong' && (
          <div className="status-message incorrect">✗ Not the winning move. Try again.</div>
        )}
        {status === 'completed' && (
          <div className="status-message completed">
            🎉 Puzzle solved!
            <div className="completion-stats">
              Time: {Math.floor((Date.now() - startTime) / 1000)}s | Attempts: {attempts + 1}
              {ratingChange && ratingChange.userDelta !== 0 && (
                <>
                  {' | '}
                  <span className={ratingChange.userDelta > 0 ? 'rating-up' : 'rating-down'}>
                    Rating: {ratingChange.userBefore} →{' '}
                    <strong>{ratingChange.userAfter}</strong>
                    {' '}({ratingChange.userDelta > 0 ? '+' : ''}{ratingChange.userDelta})
                  </span>
                </>
              )}
              {ratingChange && ratingChange.userDelta === 0 && ratingChange.alreadySolved && (
                <>{' | '}<span className="rating-note">(already solved — no rating change)</span></>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="puzzle-board-content">
        <div className="puzzle-board-play">
          <InteractiveBoard
            board={board}
            selected={selected}
            lastMove={lastMove}
            hintFrom={hintFrom}
            hintTo={hintTo}
            onSquareClick={handleSquareClick}
            disabled={status !== 'playing'}
          />
        </div>
        {hintsUsed > 0 && status === 'playing' && (
          <div className="puzzle-hint-line">
            {hintLevel === 1 && '💡 The right piece to move is highlighted.'}
            {hintLevel === 2 && '💡 The full move is highlighted (from → to).'}
            {' '}Hints used: {hintsUsed}
          </div>
        )}
        <div className="puzzle-actions">
          {status === 'playing' && turnIsUser && hintLevel < 2 && (
            <button onClick={revealHint} className="hint-button">
              {hintLevel === 0 ? '💡 Show hint (which piece?)' : '💡 Show full move'}
            </button>
          )}
          <button onClick={resetPuzzle} className="reset-button">Reset Puzzle</button>
          <button onClick={skipPuzzle} className="skip-button">Skip Puzzle</button>
        </div>
      </div>
    </div>
  );
};

export default PuzzleBoard;
